import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadUser } from "../../actions/auth";
import { trackPlausibleEvent } from "../../utils/plausible";

export default function ConfirmationPage() {
  useTranslation();
  const history = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const trackedConfirmation = useRef(false);
  const [status, setStatus]   = useState("loading"); // loading | success | error | resent
  const [errMsg, setErrMsg]   = useState("");
  const [resending, setResending] = useState(false);
  const [email, setEmail]     = useState("");

  useEffect(() => {
    if (!auth.loading && auth.isAuthenticated) {
      const currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.get("token")) {
        history("/dashboard");
        return;
      }
    }
    verifyToken();
  }, [auth.loading, auth.isAuthenticated]);

  const verifyToken = async () => {
    try {
      const url   = new URL(window.location.href);
      const token = url.searchParams.get("token");

      if (!token) {
        setStatus("error");
        setErrMsg(tr("No verification token found. Please use the link from your email."));
        return;
      }

      const config = { headers: { "Content-Type": "application/json" } };
      const res = await axios.post("/api/register/confirm", JSON.stringify({ token }), config);

      if (res.status === 200 && res.data.confirmation === true) {
        setStatus("success");
        if (!trackedConfirmation.current) {
          trackedConfirmation.current = true;
          trackPlausibleEvent("Signup Confirmed", { method: "email" });
          trackPlausibleEvent("Signup Completed", { method: "email" });
        }
        setTimeout(async () => {
          await dispatch(loadUser(res));
          history(res.data.onboarding_complete ? "/dashboard" : "/setup");
        }, 1500);
      } else {
        setStatus("error");
        setErrMsg(tr("Verification failed. Please try again."));
      }
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0]?.msg || tr("Something went wrong.");
      setStatus("error");
      setErrMsg(msg);
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!email) return;
    setResending(true);
    try {
      const config = { headers: { "Content-Type": "application/json" } };
      await axios.post("/api/register/resend", JSON.stringify({ email }), config);
      trackPlausibleEvent("Signup Confirmation Resent", { method: "email" });
      setStatus("resent");
    } catch {
      setErrMsg(tr("Could not resend email. Please try again."));
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <div style={styles.logo}>Minutezilla</div>

        {status === "loading" && (
          <>
            <div style={styles.spinner} />
            <h2 style={styles.title}>{tr("Verifying your email…")}</h2>
            <p style={styles.sub}>{tr("Just a moment while we confirm your account.")}</p>
          </>
        )}

        {status === "success" && (
          <>
            <div style={styles.iconWrap}>
              <svg style={{ width: 40, height: 40 }} viewBox="0 0 24 24" fill="none" stroke="#1a5c30" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
            <h2 style={styles.title}>{tr("Email confirmed!")}</h2>
            <p style={styles.sub}>{tr("Your account is active. Redirecting you to the dashboard…")}</p>
          </>
        )}

        {status === "error" && (
          <>
            <div style={{ ...styles.iconWrap, background: "#fef2f2" }}>
              <svg style={{ width: 40, height: 40 }} viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h2 style={{ ...styles.title, color: "#ef4444" }}>{tr("Verification failed")}</h2>
            <p style={styles.sub}>{errMsg}</p>

            <div style={styles.resendBox}>
              <p style={styles.resendLabel}>{tr("Need a new link? Enter your email:")}</p>
              <form onSubmit={handleResend} style={styles.resendForm}>
                <input
                  type="email"
                  placeholder={tr("you@example.com")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={styles.input}
                />
                <button type="submit" disabled={resending} style={styles.btn}>
                  {resending ? tr("Sending…") : tr("Resend email")}
                </button>
              </form>
            </div>

            <button onClick={() => history("/login")} style={styles.linkBtn}>{tr("Back to sign in")} </button>
          </>
        )}

        {status === "resent" && (
          <>
            <div style={styles.iconWrap}>
              <svg style={{ width: 40, height: 40 }} viewBox="0 0 24 24" fill="none" stroke="#1a5c30" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                <path d="m16 19 2 2 4-4" />
              </svg>
            </div>
            <h2 style={styles.title}>{tr("Check your inbox")}</h2>
            <p style={styles.sub}>{tr("A new verification link has been sent to")} <strong>{email}</strong>.</p>
            <button onClick={() => history("/login")} style={styles.linkBtn}>{tr("Back to sign in")} </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh",
    background: "#f4f6f4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e5e7eb",
    padding: "48px 40px",
    maxWidth: "440px",
    width: "100%",
    textAlign: "center",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  },
  logo: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#1a5c30",
    letterSpacing: "-0.5px",
    marginBottom: "32px",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "3px solid #e5e7eb",
    borderTop: "3px solid #1a5c30",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto 24px",
  },
  iconWrap: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    background: "#f0faf4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 24px",
  },
  title: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 8px",
  },
  sub: {
    fontSize: "15px",
    color: "#6b7280",
    lineHeight: 1.6,
    margin: "0 0 24px",
  },
  resendBox: {
    background: "#f9fafb",
    borderRadius: "10px",
    padding: "20px",
    marginBottom: "16px",
    textAlign: "left",
  },
  resendLabel: {
    fontSize: "13px",
    color: "#374151",
    fontWeight: 500,
    margin: "0 0 10px",
  },
  resendForm: {
    display: "flex",
    gap: "8px",
  },
  input: {
    flex: 1,
    padding: "9px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    outline: "none",
  },
  btn: {
    background: "#1a5c30",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "9px 16px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#1a5c30",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "underline",
  },
};

// Inject keyframe for spinner
if (typeof document !== "undefined" && !document.getElementById("confirm-spin-style")) {
  const style = document.createElement("style");
  style.id = "confirm-spin-style";
  style.textContent = "@keyframes spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(style);
}
